const IDLE_MS = 30_000
const TTL_MS = 30 * 24 * 60 * 60 * 1000
const UUID = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i
const ACTIONS = new Set(["play_demo", "open_editor", "sign_up"])
const label = value => /^[\w .\/-]{1,100}$/.test(value || "") ? value : ""
const pathOnly = value => value?.startsWith("/") && !value.startsWith("//") ? value.split(/[?#]/)[0].slice(0, 500) : "/"

export function firstTouch(url, stored, now, uuid) {
  const valid = touch => touch && UUID.test(touch.journey_id) && Number.isFinite(touch.started_at) &&
    touch.started_at <= now && now - touch.started_at < TTL_MS
  const p = url.searchParams
  const incoming = { journey_id: p.get("ag_journey"), started_at: Number(p.get("ag_started")),
    utm_source: p.get("ag_source"), utm_medium: p.get("ag_medium"),
    utm_campaign: p.get("ag_campaign"), landing_path: p.get("ag_landing") }
  // An explicit handoff continues the source journey even when this destination
  // already has a different local journey. Ordinary UTM links never reset it.
  let touch = p.get("ag_v") === "1" && valid(incoming) ? incoming : stored
  if (!valid(touch)) {
    touch = {
      journey_id: uuid(), started_at: now, landing_path: url.pathname,
      utm_source: p.get("utm_source"), utm_medium: p.get("utm_medium"), utm_campaign: p.get("utm_campaign")
    }
  }
  return { journey_id: touch.journey_id, started_at: touch.started_at, landing_path: pathOnly(touch.landing_path),
    utm_source: label(touch.utm_source), utm_medium: label(touch.utm_medium), utm_campaign: label(touch.utm_campaign) }
}

export function handoff(href, base, origins, touch) {
  const url = new URL(href, base)
  if (url.protocol !== "https:" && url.protocol !== "http:") return null
  if (url.origin !== new URL(base).origin && !origins.includes(url.origin)) return null
  url.searchParams.set("ag_v", "1")
  url.searchParams.set("ag_journey", touch.journey_id)
  url.searchParams.set("ag_started", String(touch.started_at))
  url.searchParams.set("ag_landing", touch.landing_path)
  for (const key of ["source", "medium", "campaign"]) {
    url.searchParams.set(`ag_${key}`, touch[`utm_${key}`])
  }
  return url.href
}

export class ActiveTime {
  constructor(now, visible) {
    this.last = this.activity = now
    this.visible = visible
    this.total = 0
  }
  tick(now) {
    if (this.visible) this.total += Math.max(0, Math.min(now, this.activity + IDLE_MS) - this.last)
    this.last = now
    return Math.floor(this.total)
  }
  interact(now) {
    this.tick(now)
    this.activity = now
  }
  visibility(now, visible) {
    this.tick(now)
    this.visible = visible
    // Returning to a page is activity; hidden time is never accrued.
    if (visible) this.activity = now
  }
}

export function ctaAction(anchor) {
  if (anchor.dataset.analyticsAction) return anchor.dataset.analyticsAction
  if (anchor.hasAttribute("data-ag-play")) return "play_demo"
  return ({ "play demo": "play_demo", "open editor": "open_editor", "sign up": "sign_up", "start free": "sign_up" })[
    anchor.textContent.trim().replace(/\s+/g, " ").toLowerCase()]
}

export function startWebsiteAnalytics() {
  const meta = document.querySelector('meta[name="website-analytics"]')
  if (!meta || meta.dataset.started) return
  meta.dataset.started = "true"
  const config = JSON.parse(meta.content)
  let stored
  try { stored = JSON.parse(localStorage.getItem("airogel:first_touch:v1")) } catch { /* storage can be disabled */ }
  const touch = firstTouch(new URL(location.href), stored, Date.now(), () => crypto.randomUUID())
  try { localStorage.setItem("airogel:first_touch:v1", JSON.stringify(touch)) } catch { /* use this page's in-memory touch */ }

  // Consume the handoff before subsequent navigation can leak it in Referer headers.
  const clean = new URL(location.href)
  for (const key of [...clean.searchParams.keys()]) if (key.startsWith("ag_")) clean.searchParams.delete(key)
  history.replaceState(history.state, "", clean.href)

  const clock = new ActiveTime(performance.now(), document.visibilityState === "visible")
  const send = (extra = {}) => {
    const body = JSON.stringify({ token: config.token, ...touch, active_ms: clock.tick(performance.now()), ...extra })
    if (!navigator.sendBeacon(config.endpoint, new Blob([body], { type: "application/json" }))) {
      fetch(config.endpoint, { method: "POST", body, headers: { "Content-Type": "application/json" },
        credentials: "same-origin", keepalive: true }).catch(() => {})
    }
  }
  const decorate = anchor => {
    if (!anchor.hasAttribute("href") || anchor.hasAttribute("download") || anchor.getAttribute("href").startsWith("#")) return
    try {
      const href = handoff(anchor.href, location.href, config.origins, touch)
      if (href && anchor.href !== href) anchor.href = href
    } catch { /* malformed authored URL */ }
  }
  const decorateAll = () => document.querySelectorAll("a[href]").forEach(decorate)
  decorateAll()
  new MutationObserver(decorateAll).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["href"] })
  const click = event => {
    if (event.type === "auxclick" && event.button !== 1) return
    const anchor = event.target.closest?.("a[href]")
    if (!anchor) return
    decorate(anchor)
    // The existing demo theme launches an iframe from data-play-url instead of
    // following href. Keep that launch on the same anonymous journey too.
    if (anchor.hasAttribute("data-ag-play")) {
      const stage = anchor.closest("[data-ag-stage]")
      if (stage) stage.dataset.playUrl = anchor.href
    }
    const action = ctaAction(anchor)
    if (ACTIONS.has(action)) send({ cta_action: action, click_id: crypto.randomUUID() })
  }
  document.addEventListener("click", click, true)
  document.addEventListener("auxclick", click, true)
  for (const name of ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"]) {
    document.addEventListener(name, () => clock.interact(performance.now()), { passive: true })
  }
  document.addEventListener("visibilitychange", () => {
    clock.visibility(performance.now(), document.visibilityState === "visible")
    send()
  })
  window.addEventListener("pagehide", () => { clock.visibility(performance.now(), false); send() })
  window.addEventListener("pageshow", event => {
    if (event.persisted) clock.visibility(performance.now(), document.visibilityState === "visible")
  })
  setInterval(() => { if (document.visibilityState === "visible") send() }, 15_000)
  send()
}
